import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { RED, WHITE_BG } from "../../utils/designTokens";

type Dot = {
  id: number;
  x: number;
  y: number;
  r: number;
  dur: number;
  delay: number;
  reverse: boolean;
};

const DEFAULT_FG_COUNT = 40;
const DEFAULT_BG_COUNT = 80;

const secureRandom = (): number => {
  const buf = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buf);
  return buf[0] / 0xFFFFFFFF;
};

interface AnimatedDotBackgroundProps {
  fgCount?: number;
  bgCount?: number;
}

const AnimatedDotBackground: React.FC<AnimatedDotBackgroundProps> = ({
  fgCount = DEFAULT_FG_COUNT,
  bgCount = DEFAULT_BG_COUNT,
}) => {
  const { fgDots, bgDots } = useMemo(() => {
    const mkDots = (count: number): Dot[] =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        x: secureRandom() * 1600,
        y: secureRandom() * 900,
        r: 1 + secureRandom() * 2,
        dur: 4 + secureRandom() * 6,
        delay: secureRandom() * 2,
        reverse: i % 2 === 0,
      }));
    return { fgDots: mkDots(fgCount), bgDots: mkDots(bgCount) };
  }, [fgCount, bgCount]);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        "& svg": { width: "100%", height: "100%" },
      }}
    >
      <svg viewBox="0 0 1600 900" preserveAspectRatio="none">
        <defs>
          <style>{`
            @keyframes drift { 0% { transform: translate(0,0); opacity: .7; } 50% { transform: translate(20px,-15px); opacity: 1; } 100% { transform: translate(0,0); opacity: .7; } }
            @keyframes driftRev { 0% { transform: translate(0,0); opacity: .7; } 50% { transform: translate(-20px,15px); opacity: 1; } 100% { transform: translate(0,0); opacity: .7; } }
          `}</style>
        </defs>

        <rect width="1600" height="900" fill={WHITE_BG} />
        <g fill={RED}>
          {bgDots.map((d) => (
            <circle
              key={d.id}
              cx={d.x}
              cy={d.y}
              r={d.r}
              style={{
                opacity: 0.2,
                animation: `${d.reverse ? "driftRev" : "drift"} ${d.dur}s ease-in-out ${d.delay}s infinite`,
              }}
            />
          ))}
          {fgDots.map((d) => (
            <circle
              key={`fg-${d.id}`}
              cx={d.x}
              cy={d.y}
              r={d.r}
              style={{
                opacity: 0.7,
                animation: `${d.reverse ? "driftRev" : "drift"} ${d.dur}s ease-in-out ${d.delay}s infinite`,
              }}
            />
          ))}
        </g>
      </svg>
    </Box>
  );
};

export default AnimatedDotBackground;
